import {stringify, _, action, autorun, computedFn, deepdash, jsondiff, observable, pluralize, React, Suspense} from '@api'
import {Popover2Props} from '@api'
import {Action, BorderNode, DefaultNodeType, FlexLayout, FlexLayoutNode, FlexMoveNodeAction, IBorderLocation, IJsonBorderNode, IJsonModel, IJsonRowNode, IJsonTabNode, IJsonTabSetNode, ITabRenderValues, ITabSetRenderValues, Layout, Model, MoveTabDirection, RowNode, SplitterNode, TabNode, TabSetNode} from '@flexlayout'
import {ChangeLogFlexLayoutTab, FlexLayoutContextMenu, ICatalogItemDetailsPathElement, IContextMenuItems, JiraRootPanel, StoresInstanceContext, WhiteboardFlexLayoutTab} from '@modules'
import {PdfOptionsPanel, PDFViewerFlexLayoutTab} from '@modules/pdf'
import {CatalogStore, globalStores, ICatalogPresetES, ICatalogUserSettings, icons, IFlexLayoutInlineCatalogItemDetailsConfig, IFlexLayoutTabProps, site, ui, WorkspaceConfig} from '@stores'
import {CatalogInstanceChildStore} from '@stores/catalog/CatalogInstanceChildStore'
import {CreateFlexLayoutStore} from '@stores/catalog/flexLayout/flexComponents/CreateFlexLayoutStore'
import {VipFlexLayoutTab, VipFlexLayoutTabStore, VipFlexLayoutTabStoreContext} from '@stores/catalog/flexLayout/flexComponents/VipFlexLayoutTab'
import {synchronizeStores} from '@stores/SynchronizeStores'
import {CentricLogo} from '@stores/ui'
import {authStore, guid} from '@utils'
import {JiraIcon} from 'centric-ui-components'
import {toHeaderCase} from 'js-convert-case'
import {computed, runInAction, toJS, trace} from 'mobx'
import {
  AggregationsFlexLayout, BuyingBoardFlexLayoutTab, BuyingBoardUrl, C8FlexLayoutTab, CatalogFiltersFlexLayout, CatalogLibrariesFlexLayout, CatalogRootFlexLayoutStore,
  CatalogRootFlexLayoutStoreContext, CatalogRootFlexLayoutTab, ChooseTabOnTabSetDropdownMenuButton, FlexLayoutCatalogItemDetailsPane, FlexLayoutInlineCatalogItemDetailsPane,
  FlexLayoutInlineCatalogItemDetailsPaneStore, FlexLayoutPresetsStore, FlexLayoutReportNavigatorStore, FlexLayoutToolboxTabWindow, IFlexLayoutCatalogRootConfig, IFlexLayoutIFrameTabConfig,
  IFlexLayoutToolbarWindowConfig, IframeFlexLayoutTab, MyFlexLayoutTabContent, MyFlexLayoutTabContentLeading, MyFlexLayoutTabPinButton, NewFlexTabButton
} from './'
import * as css from './FlexLayout.css'

export const DEBUG_FLEX_LAYOUT = false

export const FLEX_INLINE_TAB_HEIGHT = 68
export const ENABLE_TOOLWINDOW_TABSTRIPS = true
export const FLEX_WEIGHT_TOOLBOX_SIDEBAR = .2
export const FLEX_WEIGHT_DEFAULT = 1

interface IReportChanges {
  versions?: Array<{ settings: ICatalogUserSettings, timestamp: Date }>
  versionIndex?: number
}

/// region Store

type FlexComponentNameBase = 'libraries' | 'filters' | 'catalogItemDetails' | 'catalogAggregateDetails' |
  'leftSidebar' | 'jira' | 'catalog' | 'pdfOptions' | 'flexLayoutSettings' | 'c8' | 'iframe' | 'vipBuying' | 'inlineCatalogItemDetails' |
  'whiteboard' | 'change-log' | 'pdfViewer' | 'change-log-tool-window'

export type FlexComponentName = FlexComponentNameBase | string
export type FlexDetailsLocation = Extract<FlexComponentNameBase, 'inlineCatalogItemDetails' | 'catalogItemDetails'>

// const BORDER_BAR_SIZE = 8

export type IFlexJsonNode = { id: string, type?: string | any, component?: string, config?: any, children?: Array<IFlexJsonNode> }
export type FlexLayoutLookups = {
  id: {
    [id: string]: {
      children?: Array<any>
      id: string, type?: string
      config?: any
    }
  },
  component: {
    [name: string]: Array<IFlexJsonNode>
  }
  flexType: {
    [type: string]: Array<IFlexJsonNode>
  }
}

/**
 * We don't want border tabs (libraries, filters, details, aggregations, etc) to disappear off the border when shown.  Instead we will clone the tab and have a displayable one in the real layout while leaving
 * the border tab there.  This means they must have separate, but linked, IDs
 *
 * Example:  'libraries' → 'librariesTab'
 */
type BorderTabAsRealTabIdSuffix = 'Tab'

export class FlexLayoutStore extends CatalogInstanceChildStore implements IContextMenuItems {
  borderTabAsRealTabIdSuffix = 'Tab'
  borderForKnownElement: Partial<Record<FlexComponentNameBase, 'left' | 'right'>> = {
    'libraries': 'left',
    'filters': 'left',
    //'flexLayoutSettings': 'left',
    'pdfOptions': 'right',
    'catalogItemDetails': 'right',
    'catalogAggregateDetails': 'right'
    // 'change-log-tool-window': 'right'
  }

  @computed get hideTabSets() {
    const {stores: {currentUser: {userSettings: {focusMode}, anyFocusModeItemsExplicitlyChecked}}} = this
    return focusMode.enabled && (!anyFocusModeItemsExplicitlyChecked || focusMode.tabs)
  }

  layoutWhenTabSetsShownOrHidden = autorun(() => {
    if (!this._startAutoruns) return
    const {layout, hideTabSets} = this
    layout?.store && layout.store.doLayout()
  })

  @computed get availableBorderForKnownElement() {
    const {borderForKnownElement, tabsByComponent, catalog: {isClientPdfAvailable}} = this

    return _.pickBy(borderForKnownElement, (v, k) => {
      switch (k) {
        case 'change-log-tool-window':
          return !_.isEmpty(tabsByComponent?.['catalog'] ?? [])

        case 'pdfOptions':
          return isClientPdfAvailable || (DEBUG && (!_.isEmpty(tabsByComponent?.['pdfViewer']) || (tabsByComponent?.['catalog'] ?? []).some(t => t.catalogRoot?.catalog?.isShowingClientPDF ?? false)))

        case 'catalogAggregateDetails':
          return (tabsByComponent?.['catalog'] ?? []).some(t => t.catalogRoot?.catalog?.hasAggregations ?? false)

        case 'libraries': {
          return (tabsByComponent?.['catalog'] ?? []).some(t => t.catalogRoot?.catalog?.features?.library?.show !== false)
        }
        //  'libraries': 'left',
        //     'filters': 'left',
        //     //'flexLayoutSettings': 'left',
        //     'pdfOptions': 'right',
        //     'catalogItemDetails': 'right',
        //     'catalogAggregateDetails': 'right'
        default:
          return true
      }

      return false
    })
  }

  borderItemProps: { [key: string]: Partial<IJsonTabNode> } = {
    'catalogItemDetails': {}
  }

  widthForLocation = {left: undefined, right: 256}
  sidebarTabSetAttributes: Partial<IJsonTabSetNode> = {
    type: 'tabset',
    weight: FLEX_WEIGHT_DEFAULT,
    enableTabStrip: ENABLE_TOOLWINDOW_TABSTRIPS,
    enableDeleteWhenEmpty: true,
    enableDivide: false,
    enableMaximize: false,
    children: []
  }

  borderTabSetAttributes: { [key: string]: Partial<IJsonTabSetNode> } = {
    'catalogItemDetails': {
      enableTabStrip: false
    }
  }
  mainWindowFlexComponents = _.keys(this.borderForKnownElement)

  @action reset = () => {
    this.model = null
    // this.layout = null // This gets called on Layout mount and we don't have anything good to key on during report fork
    this._updatingSettings = false
  }

  @observable.ref presetsStore: FlexLayoutPresetsStore

  @observable _updateIsFromLayoutModel = false
  @observable _updateIsFromPreviewLayoutModel = false
  @observable _previewCatalogRoots: { [id: string]: CatalogRootFlexLayoutStore } = {}

  @observable.ref tabMenuNode: string
  private _showMenuNodeTimeout

  @action refetchCatalogTabs = () => {
    (this.flexLayout.tabsByComponent?.catalog ?? []).forEach(t => t?.storesToInject && t.storesToInject.catalog.refetch())
  }

  @action refetchChangeLogTabs = () => {
    const {tabsByComponent} = this,
      changeLogTabs = !tabsByComponent ? [] : tabsByComponent['change-log'] ?? []

    changeLogTabs.forEach((t: TabNode) => t.extraData?.store?.refetch())
  }

  /**
   *
   * @param includeCatalogTabs     Do you want to refetch other catalogs (including potentially the one initiating this)
   */
  @action refetchAllCatalogRelatedTabs = _.debounce((includeCatalogTabs = true) => {
    const {refetchChangeLogTabs, refetchDetailTabs, refetchCatalogTabs, refetchLibraryComponents} = this
    refetchChangeLogTabs()
    refetchDetailTabs()
    includeCatalogTabs && refetchCatalogTabs()
    refetchLibraryComponents()
  }, 100)

  /**
   * After modifying catalog items anywhere we may have out of date data in our detail panes.
   */
  @action refetchDetailTabs = () => {
    const {tabsByComponent} = this,
      detailTabs = (!tabsByComponent ? [] : tabsByComponent?.inlineCatalogItemDetails) ?? []

    detailTabs.forEach((t: TabNode) => t.extraData?.store?.overriddenStores?.sidebar?.right?.details?.loadData?.())
  }

  /**
   * After modifying catalog items anywhere we may have out of date data in our Libraries -> Some Field pane with counts
   */
  @action refetchLibraryComponents = () => {
    const {tabsByComponent} = this,
      libraryTabs = (!tabsByComponent ? [] : tabsByComponent?.libraries) ?? []

    libraryTabs.filter(t => t.catalogRoot?.catalog).forEach((t: TabNode) => {
      const {catalogRoot: {catalog: {componentStores: {itemsList}}}} = t
      itemsList?.refetch?.()
    })
  }

  @computed get catalogTabs() {
    const {model} = this

    return (model?.nodesByType?.tab ?? []).filter(t => t.component === 'catalog')
  }

  @action showTabMenuNodeDelayed = (id: string) => {
    this._showMenuNodeTimeout && clearTimeout(this._showMenuNodeTimeout)
    this._showMenuNodeTimeout = setTimeout(() => {
      this.tabMenuNode = id
    }, ui.settings.HOVER_TIMEOUT)
  }

  tabMenuInteraction = (id: string) => (nextOpenState: boolean, e?: React.SyntheticEvent<HTMLElement>) => {
    if (!nextOpenState && /*!this.showLayoutPreview &&*/ this.tabMenuNode === id) {
      this.tabMenuNode = null
    }
  }

  @observable hoveredTabSet: string = null
  /**
   * Prevent slow operations when maximizing/restoring
   */
  @observable justMaximizedOrRestored = false
  @observable animations = {maximize: 'animate__fadeIn animate__faster', restore: ''}
  @observable.ref navigator: FlexLayoutReportNavigatorStore
  @observable.ref draggingNode: FlexLayout.SplitterNode

  toggleTabMenu(node: TabNode) {
    this.tabMenuNode = this.tabMenuNode === node.id ? null : node.id
  }

  @computed get tabGroups(): { [id: string]: IJsonTabNode } {
    const {tabs} = this
    return _.keyBy(tabs.filter(t => t.isGroupLabel), t => t.id)
  }

  @action addTabToNewGroup = (tab: TabNode) => {
    const {indexInParent, tabSet, tabGroup} = tab
    if (tabGroup) {
      throw new Error(`Tab is already in group ${tabGroup}`)
    }

    const tabSetJson = tabSet.json,
      groupTab = {
        type: 'tab',
        name: 'New Group',
        groupColor: 'gray',
        groupExpanded: true,
        isGroupLabel: true,
        enableFloat: false,
        enableClose: false,
        id: guid()
      }

    tabSetJson.children.splice(indexInParent, 0, groupTab)
    tab.json.tabGroup = groupTab.id
  }

  @observable.ref previewPreset: ICatalogPresetES

  @computed get cssVariables() {
    const {model, hideTabSets} = this,
      tabStripHeight = (model?._getAttribute('tabSetTabStripHeight') ?? 36),
      borderBarSize = model?._getAttribute('borderBarSize') ?? 28

    return {
      '--tabstrip-height': `${tabStripHeight}px`,
      '--border-bar-size': `${borderBarSize}px`
    } as any
  }

  @computed get borders() {
    return _.keyBy(this.model?.borderSet?.borders, b => b.location.name)
  }

  @computed get namedItemsByBorder() {
    const {availableBorderForKnownElement} = this
    return _.groupBy(_.keys(availableBorderForKnownElement), k => availableBorderForKnownElement[k])
  }

  /// region Autoruns

  @computed get visible() {
    const {stores: {reports: {currentReport}, authStore: {userIsAuthenticated}}, flexEnabled} = this
    return userIsAuthenticated && flexEnabled && currentReport
  }

  @observable private _startAutoruns = false

  private autorun_setWhiteboardStoreToWindowW = DEBUG && autorun(() => {
    if (!this._startAutoruns || !this.stores.authStore.userIsAuthenticated) return

    window['w'] = this.activeTab?.extraData?.whiteboard
  }, {name: `autorun_setWhiteboardStoreToWindowW`})

  private autorun_showAggregationToolWindowIfActiveCatalogSaysSo = autorun(() => {
    if (!this._startAutoruns || !this.stores.authStore.userIsAuthenticated) return

    const {activeCatalogRoot} = this

    if (activeCatalogRoot?.catalog?.canShowAggs) {
      // YFTS-9752 Make sure that we open the aggregation sidebar when showAggregations is turned on
      const {catalog: {sidebar, aggregation: {isShowingDetailsInSidebar}}} = activeCatalogRoot,
        {model, isMaximized} = this

      if (!isMaximized) {
        const detailsToolWindow = model.nodeById<TabNode>('catalogAggregateDetails')
        if (detailsToolWindow) {
          _.merge(detailsToolWindow.config, {settings: {isToggledOn: isShowingDetailsInSidebar}} as IFlexLayoutToolbarWindowConfig)
        }
      } else {
        sidebar.right.open = isShowingDetailsInSidebar
      }
    }
  }, {name: 'autorun_showAggregationToolWindowIfActiveCatalogSaysSo'})

  private autorun_loadLayoutOnceConfigHasLoaded = autorun(() => {
    if (!this._startAutoruns || !this.stores.authStore.userIsAuthenticated) return
    // This is very sensitive as there's a bunch of different things going on - the user can be loading a report, the page can be loading, they can have made changes, they could change the URL to select a different report, etc
    // We only want to load the layout from the saved version when the currentReport or deployment changes
    const {presetsStore, reports: {hasLoadedRecentReports, currentReport}, stores: {deployments: {deploymentConfig}, users: {user}}} = this

    if (!user?.includesRoles) return
    // if (currentReport) {
    //   const {report: {draftedFrom: {showOriginal}}} = currentReport
    // }

    runInAction(() => {
      if (this._updatingSettings) {
        this._updatingSettings = false
      }

      if (authStore.userIsAuthenticated && deploymentConfig && hasLoadedRecentReports && currentReport) {
        //const json = _.merge({}, presetsStore.defaultModel, settings.flexLayout.model) ?? presetsStore.defaultModel

        //if (!currentReport.forkedFrom || currentReport.forkedFrom.id !== reports.forkedFromReport?.id) {
        const {settingsParsed} = currentReport, {settings} = this
        DEBUG_FLEX_LAYOUT && console.debug(`Loading flex layout settings from parsed report settings`)
        if (settingsParsed) {
          settings.maximizedTab = settingsParsed.flexLayout.maximizedTab
          settings.activeTab = settingsParsed.flexLayout.activeTab
        }

        DEBUG_FLEX_LAYOUT && console.debug(`Merging report json with default flex layout model`)
        const json = settingsParsed?.flexLayout?.model ? {...presetsStore.defaultModel, ...settingsParsed.flexLayout.model} : presetsStore.defaultModel

        //console.log({settingsStore, settings: settings?.flexLayout})
        json.global = presetsStore.defaultModel.global
        this.modelJson = json

        // Todo - this is slow as it causes us to save off the report to the backend even though nothing has changed
        settings.model = this.modelJson
        this._updateLayoutFromJsonModel()

        DEBUG_FLEX_LAYOUT && console.debug(`Finished loading flex layout from current report`)
        DEBUG_FLEX_LAYOUT && console.groupEnd()
      }
    })
  }, {name: `Load layout json once initial workspace settings have loaded`})

  private autorun_fixupEmptyModel = autorun(() => {
    if (!this._startAutoruns || !this.stores.authStore.userIsAuthenticated) return
    const {model} = this
    if (model?.root && model.root.children.filter(r => !r.isLayoutSidebar).length == 0) {
      model.root.addChildJson({
        type: 'tabset'
      } as IJsonTabSetNode, model.root.children.length == 2 ? 1 : 0) // Insert after potential left sidebar
    }
  }, {name: 'Fixup empty model'})

  private autorun_resetOnLogout = autorun(() => {
    if (!this._startAutoruns || !this.stores.authStore.userIsAuthenticated) return

    const {stores: {authStore: {userIsAuthenticated}}} = this

    if (!userIsAuthenticated) {
      runInAction(() => {
        DEBUG_FLEX_LAYOUT && console.debug(`Clearing model/stores/etc`)

        this.model = null
      })
    }
  }, {name: `Reset on logout`})

  @computed get activeTabId() { return this.settings.activeTab}

  private autorun_updateWindowObjectForDev = autorun(() => {
    if (!DEBUG || !this._startAutoruns || !this.stores.authStore.userIsAuthenticated) return

    const {activeTab, activeCatalogRoot} = this

    window['activeTab'] = activeTab
    window['acr'] = activeCatalogRoot
  }, {name: `Update window object for dev`})

  private autorun_setActiveTabAsNeeded = autorun(() => {
    if (!this._startAutoruns || !this.stores.authStore.userIsAuthenticated) return

    const {model} = this
    if (model?.activeTabset) {
      const {activeTabset: {selectedNode}} = model, {settings, isMaximized} = this
      if (selectedNode && !this.knownTabs[selectedNode.id]) {
        if (this.activeTabId != selectedNode.id) {
          this.settings.activeTab = selectedNode.id
        }

        if (isMaximized) {
          settings.maximizedTab = selectedNode.id
        }
      }
    }
  }, {name: `Set active tab as needed`})

  // private autorun_setLoadingWhenUrlChanges = autorun(() => {
  //   if (!this._startAutoruns || !this.stores.authStore.userIsAuthenticated) return
  //
  //   const {currentReport: {nav:{queryParams: {id}}}} = this
  //   if (id) {
  //     this.model = null
  //   }
  // }, {name: `Turn off flex layout if config says no (ASICS)`})

  private autorun_closeTabMenuIfInvalid = autorun(() => {
    if (!this._startAutoruns || !this.stores.authStore.userIsAuthenticated) return

    const {tabMenuNode, model} = this
    if (tabMenuNode && model && !model.nodeById(tabMenuNode)) {
      this.tabMenuNode = null
    }
  }, {name: 'Close tab menu if invalid'})

  @computed get tabsByComponent(): Record<FlexComponentName, Array<TabNode>> {
    const {tabNodes} = this
    return _.groupBy(tabNodes, t => t.component)
  }

  /**
   * After we have toggled some known tabs on such as
   *
   * -----------------------
   * | Libraries |         |
   * ------------| Catalog |
   * | Filters   |         |
   * -----------------------
   *
   * If the user Xs one of the tabs or drags them around we will lose our 'leftSidebar' id in the flex layout and no longer be able to correctly toggle things on and off
   * This autorun tries to detect these sorts of conditions and adjust IDs as needed
   * @private
   */
  private autorun_addOrRemoveSidebarsAsNeeded = autorun(() => {
    if (!TEST && (!this._startAutoruns || !this.stores.authStore.userIsAuthenticated || !this.layout?.store?.hasDoneLayoutPass)) return

    const {sidebarTabSetAttributes, widthForLocation, presetsStore: {createKnownElement}, tabsByComponent, availableBorderForKnownElement, borderTabAsRealTabIdSuffix, model, namedItemsByBorder, borders, knownTabs, removeNodeFromParent} = this

    if (!model || this.activeTab?.storesToInject?.catalog?._isFirstLoad) return

    const toModify = _.chain(availableBorderForKnownElement).values().uniq().map(b => borders[b])
      .compact()
      .value()
      .map(b => {
        const {location: {name: location}} = b,
          namedItems = namedItemsByBorder[location],
          borderNodes = namedItems.map(id => model.nodeById<TabNode<IFlexLayoutToolbarWindowConfig>>(id)).filter(_.identity),
          namedBorderNodesInOrder = _.orderBy<IJsonTabNode>(namedItems.map(createKnownElement), 'sortIndex'),
          correctVisibleSidebarItems = borderNodes.filter(node => node.config?.settings?.isToggledOn && !_.isEmpty(tabsByComponent['catalog'])) // don't close toolbox items - it's annoying - && (!this.isDisabled(node) || location === 'right') -  right sidebar items follow focus, don't auto-remove them

        return {namedItems, borderNodes, namedBorderNodesInOrder, border: b, correctVisibleSidebarItems}
      })

    runInAction(() => {
      toModify.forEach(({namedBorderNodesInOrder, namedItems, correctVisibleSidebarItems, border: b}) => {
        const {location: {name: location}, isCollapsed} = b,
          sidebarName = `${location}Sidebar`,
          shouldSidebarBeShowing = !_.isEmpty(correctVisibleSidebarItems)

        let sidebarNode = model.nodeById(sidebarName)

        // Make sure that all named items are actually added to their associated border
        namedBorderNodesInOrder.forEach(jsonNode => {
          if (!model._idMap[jsonNode.id]) {
            DEBUG_FLEX_LAYOUT && console.debug(`Adding ${jsonNode.id} to ${location} border...`)

            const node = TabNode._fromJson(jsonNode, model)
            node.parent = b
            if (_.isNumber(jsonNode.sortIndex) && jsonNode.sortIndex < b.children.length) {
              b._children.splice(jsonNode.sortIndex, 0, node)
            } else {
              b._children.push(node)
            }
          }
        })

        const currentlyVisibleSidebarItems = namedItems.map(item => knownTabs[`${item}${borderTabAsRealTabIdSuffix}`]).filter(_.identity),
          toRemove = [...b._children.filter(tab => !availableBorderForKnownElement[tab.id]), ...currentlyVisibleSidebarItems.filter(t => !correctVisibleSidebarItems.some(c => c.component == t.component))]

        toRemove.forEach((tab: TabNode) => {
          DEBUG_FLEX_LAYOUT && console.debug(`Remove ${tab.id} as it is no longer valid to display`)
          if (tab.associatedToolboxBorderTab) {
            tab.associatedToolboxBorderTab.config.settings.isToggledOn = false
          }
          removeNodeFromParent(tab, true)
        })

        // console.debug({location, correctVisibleSidebarItems, toRemove, currentlyVisibleSidebarItems})
        if (shouldSidebarBeShowing && !sidebarNode) {
          DEBUG_FLEX_LAYOUT && console.debug(`We should have a ${location} sidebar, but do not.  Creating one...`, {correctVisibleSidebarItems, shouldSidebarBeShowing, namedItems, currentlyVisibleSidebarItems})
          // Remove the existing tabs
          currentlyVisibleSidebarItems.forEach(tab => removeNodeFromParent(tab, true))

          const {rootRow} = this

          switch (location) {
            case 'left':
            case 'right':
              const index = location === 'left' ? 0 : rootRow.children.length
              if (currentlyVisibleSidebarItems.length === 1) {
                // Sidebar is a TabSet in top-level row
                const node = TabSetNode._fromJson({
                  weight: rootRow.normalizeWeight(FLEX_WEIGHT_TOOLBOX_SIDEBAR),
                  id: sidebarName,
                  ...sidebarTabSetAttributes,
                  children: []
                }, model)
                node.parent = rootRow
                rootRow._children.splice(index, 0, node)
              } else {
                // Sidebar is a Row in top-level row with multiple tabsets within

                const rowNode = RowNode._fromJson({
                  id: sidebarName,
                  type: 'row',
                  weight: rootRow.normalizeWeight(FLEX_WEIGHT_TOOLBOX_SIDEBAR),
                  width: widthForLocation[location],
                  children: []
                } as IJsonRowNode, model)
                rowNode.parent = rootRow

                //currentlyVisibleSidebarItems
                rootRow._children.splice(index, 0, rowNode)
              }
              break

            default: {
              DEBUG_FLEX_LAYOUT && console.warn(`Unhandled case ${location}`)
            }
          }
        } else if (!shouldSidebarBeShowing && sidebarNode) {
          DEBUG_FLEX_LAYOUT && console.debug(`Remove ${location} sidebar`, {correctVisibleSidebarItems, currentlyVisibleSidebarItems, sidebarNode})
          removeNodeFromParent(sidebarNode, true)
        }

        if (shouldSidebarBeShowing) {
          let sidebarNode = model.nodeById<RowNode>(sidebarName)

          let children = []

          if (sidebarNode.children.length != correctVisibleSidebarItems.length) {
            // Move each tab into its own individual tabset
            correctVisibleSidebarItems.forEach(tab => {
              const id = `${tab.id}${borderTabAsRealTabIdSuffix}`,
                existingTab = model.nodeById<TabNode>(id)

              if (existingTab) {
                children.push(existingTab.parent)
              } else {
                const tabSet = TabSetNode._fromJson(sidebarTabSetAttributes, model)
                tabSet.parent = sidebarNode
                children.push(tabSet)

                tabSet.addChildJson({
                  ...tab,
                  enableRename: false,
                  component: tab.id,
                  enableDrag: true,
                  enableClose: true,
                  id
                })
              }
            })

            sidebarNode._children = children
          }
        }
      })

      this.layout?.store?.doLayout()
    })
  }, {
    name: `Create/destroy toolbox items and their tabsets/sidebars as needed`
    //delay: 50 // Allow a paint for the color to change in the sidebar
  })

  private autorun_setEverMaximized = autorun(() => {
    if (!this._startAutoruns || !this.stores.authStore.userIsAuthenticated) return
    if (!this.everMaximized && this.isMaximized) {
      this.everMaximized = true
      this.autorun_setEverMaximized()
    }
  }, {name: `autorun_setEverMaximized`})

  private autorun_trackLastMaximized = autorun(() => {
    if (!this._startAutoruns || !this.stores.authStore.userIsAuthenticated) return
    const {maximizedTab} = this
    runInAction(() => {
      const {lastMaximizedTab, layout, model, maximizedTabSet} = this

      maximizedTab && !maximizedTab.isSelected && maximizedTab.selectInTabSet()
      this.justMaximizedOrRestored = (lastMaximizedTab === null && maximizedTab != null) || (maximizedTab == null && lastMaximizedTab != null)
      this.lastMaximizedTab = maximizedTab || lastMaximizedTab
    })
  }, {name: 'Track lack maximized tab and whether we just maximized or restored'})

  // private autorun_clearLayoutOnDeploymentChange = autorun(() => {
  //   if (!this._startAutoruns || !this.stores.authStore.userIsAuthenticated) return
  //   const {stores: {deployments: {selectedDeploymentId}}} = this
  //
  //   if (selectedDeploymentId) {
  //     const {_catalogRoots} = this
  //
  //     _.forEach(_catalogRoots, (store, id) => {
  //       if (store.config.deployment !== selectedDeploymentId) {
  //         console.log(`Removing tab ${id} - ${store.tab?.name} as it is`)
  //       }
  //
  //     })
  //
  //   }
  //   runInAction(`Remove tabs that are no longer targeted by our deployment`,() => {
  //       this.settings.model = undefined
  //     _.values(this._catalogRoots).forEach(s => s.dispose())
  //     this._catalogRoots = {}
  //   })
  // })

  private autorun_fixupGroupTabsAreCorrect = autorun(() => {
    if (!this._startAutoruns || !this.stores.authStore.userIsAuthenticated) return
    const {tabGroups, model} = this
    if (!model || _.isEmpty(tabGroups)) return

    const {tabSets, tabsByTabGroup} = this

    _.values(tabGroups).forEach(group => {
      const labelTab = model.nodeById<TabNode>(group.id)

      if (labelTab) {
        const {tabSet} = labelTab,
          tabs = tabsByTabGroup[group.id]

        if (_.isEmpty(tabs)) {
          DEBUG_FLEX_LAYOUT && console.debug(`Tab Group has no associated tabs`) // - removing`)
          // this.removeNodeFromParent(labelTab)
        } else {
          const tabsToMove = tabs.map(t => model.nodeById<TabNode>(t.id)).filter(t => t.tabSet !== tabSet || t.indexInParent < labelTab.indexInParent || t.indexInParent > labelTab.lastContiguousGroupTabIndex)

          if (!_.isEmpty(tabsToMove)) {
            DEBUG_FLEX_LAYOUT && console.log(`Tab Group '${group.name}' - ${group.id} - ${tabs ? tabs.length : 0} tabs`, toJS({tabs, group, tabsToMove}))

            // debugger
            tabsToMove.forEach(t => {
              DEBUG_FLEX_LAYOUT && console.debug(`Moving tab ${t.id} to be in the same tab set as the tab group it belongs to ${t.tabGroup}`, toJS({t, labelTab}))
              this.removeNodeFromParent(t)
              tabSet.addChild(t, labelTab.indexInParent + 1)
            })
          }
        }

        if (labelTab.groupExpanded) {
          if (tabSet.selected === labelTab.indexInParent) {
            tabSet._setSelected(labelTab.indexInParent + 1)
          }
        }

        if (tabSet._dirty) {
          this.layout?.forceUpdate()
        }
      }
    })
  }, {name: `Fixup tab lists so that tabs are with their tab groups`})

 /* private _updatePreviewLayoutFromModel = autorun(() => {
    if (!this._startAutoruns || !this.showLayoutPreview) return
    const {previewModelJson} = this

    const json = _.cloneDeep(previewModelJson)
    deepdash.eachDeep(json, (v, k, parent, content) => {
      if (k === 'id' && parent.type && v && v.startsWith('#')) {
        delete parent[k]
      }
    })

    const diff = jsondiff.diff(json, this.previewModel?.asJson)

    diff && runInAction(() => {
      if (diff || !this.previewModel) {
        // this.previewModel && DEBUG && console.debug(`Reset PREVIEW flex layout from JSON change`, {previewModelJson: toJS(previewModelJson), diff})
        const newModel = FlexLayout.Model.fromJson(json, true, `update preview model`)
        this.previewModel = newModel
        this._updateIsFromPreviewLayoutModel = true
        this.previewModelJson = newModel.asJson
      }
    })
  }, {name: `_updatePreviewLayoutFromModel`})
*/

 /* private autorun_setupPreviewLayout = autorun(() => {
    if (!this._startAutoruns || !this.stores.authStore.userIsAuthenticated) return
    const {previewPreset, previewLayoutInteractionNode, presetsStore} = this
    let json: any
    if (previewLayoutInteractionNode) {
      // Done directly inline when setting the node
    } else if (previewPreset?.layout && previewPreset._id != this.settings.preset) {
      //this.previewModel = Model.fromJson(_.merge({}, presetsStore.defaultModel, JSON.parse(previewPreset.layout), {borders: []}))
      json = {...presetsStore.defaultModel, ...JSON.parse(previewPreset.layout)}
      this.previewModelJson = json
      this.previewModel = Model.fromJson(json, true, `Create preview layout from ES json`)
    }
  }, {name: `Setup preview layout when moused over presets/menu actions`})*/

  /// endregion

  @computed get flexEnabled() {
    return true //this.settings?.enabled === true
  }

  constructor(public catalog: CatalogStore) {
    super(catalog)

    // console.log(`Construct FlexLayoutStore`)
    synchronizeStores(() => {
      if (!this.catalog.stores.isGlobalStore) throw new Error(`RootCatalogFlexLayoutStore is expected to be a singleton`)

      this.create = new CreateFlexLayoutStore(this)
      const {stores} = this
      if (stores) {
        stores.flexLayout = this
      }

      const {catalog} = stores

      this.presetsStore = new FlexLayoutPresetsStore(catalog, this)

      // if (!TEST) {
      this._startAutoruns = true
      // }

      // Useful for debugging
      // observe(this, 'modelJson', ({oldValue}: any) => {
      //   console.log(oldValue, this.modelJson)
      // });
    })
  }

  // @computed get lookups() {
  //   return this.model?.lookups ?? {
  //     id: {},
  //     component: {},
  //     flexType: {}
  //   }
  // }

  @computed get maximizedTabSet() {
    return this.maximizedTab?.tabSet
  }

  private _tabsInOrderRecurse = (node: FlexLayoutNode) => {
    if (node instanceof TabNode) return node

    return node.children ? _.flatMap(node.children.map(v => this._tabsInOrderRecurse(v))) : []
  }

  @computed get activeTabIndex() {
    const {tabsInOrder, activeTab} = this
    if (!activeTab) return -1
    return tabsInOrder.indexOf(activeTab)
  }

  @computed get tabsInOrder(): Array<TabNode> {
    const {model, _tabsInOrderRecurse, knownTabs} = this
    if (!model) return []

    return _.flatMap(model.root.children.reduce((acc, node) => [...acc, _tabsInOrderRecurse(node)], [])).filter(tab => !knownTabs[tab.id])
  }

  @computed get firstTab() {
    return _.first(this.tabsInOrder)
  }

  @computed get lastTab() {
    return _.last(this.tabsInOrder)
  }

  @computed get nextTab(): TabNode {
    const {activeTabIndex, tabsInOrder, firstTab} = this
    return (activeTabIndex != -1 && activeTabIndex + 1 < tabsInOrder.length) ? tabsInOrder[activeTabIndex + 1] : firstTab
  }

  @computed get previousTab(): TabNode {
    const {activeTabIndex, tabsInOrder, lastTab} = this
    return (activeTabIndex > 0) ? tabsInOrder[activeTabIndex - 1] : lastTab
  }

  @observable everMaximized = false

  @computed get isMaximized() {
    if (!this.catalog?.settingsStore?.settings) return false
    return this.maximizedTab != null || this.maximizedTabSet != null || !this.flexEnabled
  }

  @computed get maximizedTab(): TabNode {
    if (!this.catalog?.settingsStore?.settings) return undefined

    const {settings: {maximizedTab}, model} = this
    return maximizedTab && model ? model.nodeById<TabNode>(maximizedTab) : undefined
  }

  set maximizedTab(tab: TabNode) {
    DEBUG_FLEX_LAYOUT && console.log(`Maximize tab - ${tab.id}`)
    this.settings.maximizedTab = tab.id
  }

  @observable lastMaximizedTab: TabNode

  @action maximized_goToFirstTab = () => {
    const {maximizeTab, firstTab} = this
    maximizeTab(firstTab.id)
  }

  @action maximized_goToPreviousTab = () => {
    const {maximizeTab, previousTab} = this
    maximizeTab(previousTab.id)
  }

  @action maximized_goToNextTab = () => {
    const {maximizeTab, nextTab} = this
    maximizeTab(nextTab.id)
  }

  @action maximized_goToLastTab = () => {
    const {maximizeTab, lastTab} = this
    maximizeTab(lastTab.id)
  }

  @computed get showCatalogWidgetsInTopBar() {
    return /*this.activeCatalogTabs.length <= 1 ||*/ this.isMaximized
  }

  // Expensive
  /*@computed get activeCatalogTabs() {
    const {modelJson: {borders, layout}, _catalogRoots} = this,
      nodes = [
        ...borders.map(b => b.show !== false && b.selected >= 0 ? b.children[b.selected] : _.first(b.children)).filter(v => v?.type == 'tab' && v.component === 'catalog'),
        ...deepdash.mapDeep(layout.children, (v, k, parent) => {
          if (k == 'type' && v == 'tabset') {
            const node = parent as IJsonTabSetNode

            if (!_.isEmpty(node.children)) {
              const child = node.children[node.selected ?? 0] as IJsonTabNode

              if (child && child.component === 'catalog') {
                return _catalogRoots[child.id]
              }
            }
          }
          return null
        })
      ].filter(_.identity)

    //console.log(toJS(nodes))
    return nodes
  }*/

  @computed get isPdfInUse() {
    const {model} = this
    if (!model) return false
    return model.nodesByType.tab.some(t => t.component === 'catalog' && t?.attributes?.config?.settings?.catalog?.viewMode === 'pdfViewer') === true
  }

  @computed get tabsByTabGroup() {
    return _.groupBy(this.tabs.filter(t => t.tabGroup), t => t.tabGroup)
  }

  createAndShowDock = (border: IBorderLocation): IJsonBorderNode => {
    const propName = `border${_.capitalize(border)}`

    if (!_.has(this, propName)) {
      DEBUG_FLEX_LAYOUT && console.warn(`Unknown border type '${border}'`)
      return null
    } else {
      let node = this[propName] as IJsonBorderNode,
        {presetsStore, modelJson: {borders}} = this

      if (!node) {
        // Create the dock
        node = {
          ...presetsStore.borderJson,
          location: border
        }

        borders.push(node)
      } else {
        // Dock already exists
        node.show = true
      }

      return node
    }
  }

  @computed get isShowingCatalogItemDetailsTab() {
    return this.model.nodeById<TabNode<IFlexLayoutToolbarWindowConfig>>('catalogItemDetails')?.config?.settings?.isToggledOn === true
  }

  @computed get isShowingSettingsTab() {
    return this.model.nodeById<TabNode<IFlexLayoutToolbarWindowConfig>>('flexLayoutSettings')?.config?.settings?.isToggledOn === true
  }

  @computed get isShowingPDFOptionsTab() {
    return this.model.nodeById<TabNode<IFlexLayoutToolbarWindowConfig>>('pdfOptions')?.config?.settings?.isToggledOn === true
  }

  @computed get isShowingLibraries() {
    const {maximizedTab, model} = this
    if (maximizedTab?.catalogRoot) {
      const {catalogRoot: {stores: {sidebar: {left}}}} = maximizedTab

      return left.open && left.tab === 0
    }

    return model.nodeById<TabNode<IFlexLayoutToolbarWindowConfig>>('libraries')?.config?.settings?.isToggledOn === true
  }

  @computed get isShowingFilters() {
    const {maximizedTab, model} = this
    if (maximizedTab?.catalogRoot) {
      const {catalogRoot: {stores: {sidebar: {left}}}} = maximizedTab

      return left.open && left.tab === 1
    }

    return model.nodeById<TabNode<IFlexLayoutToolbarWindowConfig>>('filters')?.config?.settings?.isToggledOn === true
  }

  @computed get isShowingRightBorderItems() {
    const {knownTabs, borderForKnownElement, borderTabAsRealTabIdSuffix} = this
    return _.keys(borderForKnownElement).some(k => borderForKnownElement[k] === 'right' && knownTabs[`${k}${borderTabAsRealTabIdSuffix}`] != null)
  }

  @computed get knownTabs(): Partial<Record<FlexComponentNameBase | `${FlexComponentNameBase}${BorderTabAsRealTabIdSuffix}`, TabNode>> {
    const {model, borderTabAsRealTabIdSuffix} = this
    if (!model) return {}

    return _.keys(this.borderForKnownElement).reduce((acc, k: FlexComponentNameBase) => ({
      ...acc,
      [k]: model.nodeById<TabNode>(k),
      [`${k}${borderTabAsRealTabIdSuffix}`]: model.nodeById<TabNode>(`${k}${borderTabAsRealTabIdSuffix}`)
    }), {})
  }

  @observable.ref model: Model

  // registerEffects = () => {
  //
  //   const toDispose = []
  //
  //   return () => toDispose.forEach(f => f())
  // }

  @computed get activeTabs(): Array<TabNode> {
    const {model, tabs, knownTabs} = this

    if (!model) return []
    return tabs.map(t => model.nodeById<TabNode>(t.id)).filter(t => t && t?.isInTabSet && t?.isSelected && !knownTabs[t.id])
  }

  @computed get catalogRoots(): Array<CatalogRootFlexLayoutStore> {
    return _.uniq(this.tabNodes.map(t => t?.catalogRoot).filter(v => v))
  }

  @observable.ref lastActiveCatalogRoot: CatalogRootFlexLayoutStore

  @computed get activeOrLastCatalogRoot(): CatalogRootFlexLayoutStore {
    const {activeTabs, activeCatalogRoot, lastActiveCatalogRoot} = this
    return activeCatalogRoot ?? lastActiveCatalogRoot ?? activeTabs.find(t => t.isVisible && t.catalogRoot)?.catalogRoot ?? activeTabs.find(t => t.catalogRoot)?.catalogRoot
  }

  @computed get activeOrLastCatalogRootId() { return this.activeOrLastCatalogRoot?.catalog?.id}

  trackLastActiveCatalogRoot = autorun(() => {
    if (!this._startAutoruns) return

    const {activeCatalogRoot} = this
    if (activeCatalogRoot) {
      this.lastActiveCatalogRoot = activeCatalogRoot
    }
  })

  @computed get activeCatalogRoot(): CatalogRootFlexLayoutStore {
    const {activeTab} = this

    // Leads to invalid sidebar details behavior
    // if (activeTab?.component === 'inlineCatalogItemDetails') {
    //   if (activeTab?.extraData?.store?.parentTab?.catalogRoot) {
    //     return activeTab?.extraData?.store?.parentTab.catalogRoot
    //   }
    // }

    return activeTab?.catalogRoot
  }

  @computed get activeTab(): TabNode {
    const r: TabNode = this.maximizedTab ?? this.model?.nodeById<TabNode>(this.activeTabId) ?? _.first(this.activeTabs)
    if (!r?.isVisible) return _.first(this.activeTabs)
    return r
  }

  set activeTab(tab: TabNode) {
    const {tabSet, indexInParent} = tab
    tabSet.model._setActiveTabset(tabSet)
    tabSet._setSelected(indexInParent)

    if (!this.knownTabs[tab.id]) { // Don't set toolbox windows as the 'active tab'
      this.settings.activeTab = tab.id

      if (this.isMaximized) {
        this.maximizedTab = tab
      }
    }
  }

  @observable.ref layout: Layout
  setRef = layout => this.layout = layout

  @observable modelHasChanged = false

  onModelChange = _.debounce(action((model: Model) => {
    if (this._updatingSettings) {
      // console.debug(`onModelChange - ignore change as we initiated this via a change to our settings`)
      this._updatingSettings = false
      // return
    }

    const modelJson = model.asJson

    // Remove internal id fields that start with # or else this will break later on deserialize
    // const diff = jsondiff.diff(modelJson, this.modelJson)
    // console.log(`onModelChange - expensive compare`, {modelJson, diff})
    //
    // if (diff) {
    this._updateIsFromLayoutModel = true
    this.modelJson = modelJson

    this._updateSettingFromModelJson()  /** {@see ReportsStore.autorun_updateEsReportWhenChanged} **/
    // } else {
    //   // console.log(`skip - no change detected in settings`)
    // }
  }), 500)

  @observable.ref previewLayout: Layout
  setPreviewLayoutRef = layout => this.previewLayout = layout

  @observable.ref previewModel: Model
  @observable previewModelJson: IJsonModel

  @action onPreviewModelChange = (previewModel: Model) => {
    const modelJson = previewModel.asJson
    this.previewModelJson = modelJson
  }

  defaultPopoverMenuProps: Partial<Popover2Props> = {
    interactionKind: 'hover',
    usePortal: true,
    lazy: true,
    portalClassName: css.menuPortal,
    hoverOpenDelay: 500,
    hoverCloseDelay: 600,
    minimal: true,
    modifiers: {
      offset: {
        enabled: true,
        options: {
          offset: [0, 2]
        }
      },
      flip: {
        enabled: true
      },
      preventOverflow: {
        enabled: true
      }
    },
    position: 'bottom-left'
  }

  @computed get showLayoutPreview() {
    const {previewLayoutInteractionNode} = this
    if (previewLayoutInteractionNode) return true

    return false
    //return previewPreset != null && previewPreset._id != settings.preset
  }

  @observable.ref modelJson: IJsonModel

  iconFor = (node: TabNode) => {
    const {component} = node

    switch (component) {
      case 'change-log-tool-window':
        return 'delta'

      case 'c8':
        return <CentricLogo/>

      case 'change-history':
        return icons.changeHistory

      case 'whiteboard':
        return icons.whiteboard

      case 'pdfViewer':
        return icons.pdf

      case 'flexLayoutSettings':
        return icons.flexLayout
      case 'pdfOptions':
        return icons.pdf
      case 'jira':
        return <JiraIcon/>
      case 'filters':
        return icons.filter
      case 'libraries':
        return icons.catalogLibrary
      case 'catalogItemDetails': {
        return icons.switchDetailViewLocation
      }

      case 'inlineCatalogItemDetails': {
        const {extraData} = node
        //console.log(extraData)

        if (extraData?.store) {
          const store = extraData.store as FlexLayoutInlineCatalogItemDetailsPaneStore
          return store.tabIcon
        }
        return icons.switchDetailViewLocation
      }

      case 'catalogAggregateDetails':
        return icons.aggregateDetails
    }
    return null
  }

  onRenderTab = (node: TabNode, renderValues: ITabRenderValues) => {
    const component = node.component,
      icon = this.iconFor(node)

    // console.log(`onRenderTab`, node, renderValues, {id, parentType, icon})

    renderValues.content = <MyFlexLayoutTabContent node={node}/>
    renderValues.leading = <MyFlexLayoutTabContentLeading node={node}/>

    //renderValues.buttons.push(<FlexLayoutCatalogTabViewModeDropdownToolbarMenu key='viewMode' node={node} />)

    if (component === 'catalog') {
      const {pin} = node.attributes.config as IFlexLayoutCatalogRootConfig

      if (pin) {
        renderValues.buttons.length = 0
        renderValues.buttons.push(<MyFlexLayoutTabPinButton key='pin' node={node}/>)
      }

      // renderValues.buttons.push(<SyncWorkspaceTabMenuButton key='sync'/>)
      //renderValues.buttons.push(<FilterCatalogTabDropdownToolbarMenu key='filter'/>)
    }

    // Don't allow closing of special items docked to the sidebar
    // if (parentType === 'border' && _.includes(['libraries', 'filters'], id)) {
    //   renderValues.buttons = []
    // }
    // console.log(`after`, renderValues)
    //renderValues.buttons = [<Icon key={1} icon='ninja' />]
  }

  get sidebar() { return this.stores.sidebar}

  @computed get customMenuOpen() { return this.tabMenuNode != null}

  @action customTabMenuOpening = (nodeId?: string) => {
    this.tabMenuNode = _.isString(nodeId) ? nodeId : null
  }

  @action customTabMenuClosing = () => {
    this.tabMenuNode = null
    this.clearPreviewLayoutInteractionNode()
  }

  @action onAction = (action: Action) => {
    const {model, sidebar, layout, borderForKnownElement} = this,
      {type, data} = action,
      id = data.tabNode ?? data.node ?? data.fromNode,
      node = model.nodeById(id)

    model.lastAction = action.type

    // DEBUG && console.log(`[FlexLayout] - onAction - [${type}] - [${id}] - [${(node?.attributes as any)?.name}]`, {action, type, id})

    if (type === 'FlexLayout_DeleteTab' || type === 'FlexLayout_RenameTab' || type === 'FlexLayout_MaximizeToggle' || type == 'FlexLayout_AddNode') {
      this._updatingSettings = false
    }

    if (type === 'FlexLayout_AdjustSplit' || type === 'FlexLayout_SetActiveTabset') {
      this._updateIsFromLayoutModel = true
      site.activePopover?.reposition?.()
    }

    if (type === 'FlexLayout_DeleteTab') {
      const tab = node as TabNode, {borderTabAsRealTabIdSuffix} = this

      if (tab && tab.id.endsWith(borderTabAsRealTabIdSuffix)) {
        const id = tab.id.substring(0, tab.id.length - borderTabAsRealTabIdSuffix.length)
        if (borderForKnownElement[id]) {
          this.toggleKnownTab(id, false)
        }
      }

      site.activePopover?.reposition?.()
    }

    if (type === 'FlexLayout_RenameTab') {
      const name = data.text,
        tab = node as TabNode,
        config = tab.config as IFlexLayoutCatalogRootConfig

      tab.config.nonDefaultName = name != null
      tab._setName(name)
    }

    if (type === 'FlexLayout_MoveNode') {
      const {fromNode, toNode, location, index, select} = action.data as FlexMoveNodeAction,
        tab = node as TabNode

      if (tab?.associatedGroup) {
        if (index === -1) {
          return undefined
        }

        if (!tab.isGroupLabel) {
          // If the node has been moved outside of our group, ungroup it

          debugger
        }
      }

      this._updatingSettings = false
      if (location === 'center') {
        // For Tab Group [A, B, C] with A selected, moving B to after C causes C to become selected and B to have an improper render (NEITHER content window should have needed to load)

        action.data.select = false
      }

      site.activePopover?.reposition?.()
    }

    if (!node || node.type !== 'tab') return action

    const tabNode = node as TabNode, component = tabNode.component

    if (type == 'FlexLayout_SelectTab') {
      site.activePopover?.reposition?.()
      if (layout?.store?.editingTab === tabNode) {
        return undefined
      }

      // Clicks on our custom menu cause selection event to fire for the tab which causes double renders, eat the event if we have an open menu
      if (this.customMenuOpen) {
        // If we are already the selected tab go ahead and eat the event
        if (tabNode.tabSet?.selected === tabNode.indexInParent) {
          return undefined
        }
      }

      if (!borderForKnownElement[tabNode.id]) {
        // Set active tab if it's a content tab and not just a border element like 'libraries'
        this.setActiveTab(tabNode)
      } else {
        // This is a toolbox window, handle the event ourselves and toggle the tab on
        this.toggleKnownTab(id)
        return undefined
      }

      // Already selected - don't do anything.  Otherwise double-click usually doesn't work
      if (tabNode.isSelected) return undefined
    } /*else if (type == 'FlexLayout_DeleteTab' && _.includes(['filters', 'libraries'], id)) {
      this.toggleTab(id)
      return undefined
    }*/

    // switch (type) {
    //   case 'FlexLayout_SelectTab': {
    //     'libraries'
    //     break
    //   }
    //
    //   case 'FlexLayout_DeleteTab': {
    //     switch (id) {
    //       case 'filters':
    //         this.isShowingFilters = false
    //         break
    //
    //       case 'libraries':
    //         this.isShowingLibraries = false
    //         break
    //
    //       default: {
    //         break
    //       }
    //     }
    //
    //     break
    //   }
    //
    //   default: {
    //     break
    //   }
    // }

    return action
  }

  iconFactory = (node: TabNode) => {
    return null
  }

  onExternalDrag = (event: React.DragEvent<HTMLDivElement>) => {

    //   undefined | {
    //   dragText: string,
    //   json: any,
    //   onDrop?: (node?: Node, event?: Event) => void
    // }
  }

  onRenderTabSet = (tabSet: TabSetNode | BorderNode, renderValues: ITabSetRenderValues) => {

    if (tabSet instanceof TabSetNode && !tabSet.isLayoutSidebar) {
      renderValues.stickyButtons = [
        <NewFlexTabButton key='new' tabSet={tabSet}/>
      ]

      // if (tabSet.canMaximize) {
      //   renderValues.buttons.push(
      //     <HideTabsetToggleButton key='hideTabset' tabSet={tabSet}/>,
      //     <MaximizeTabSetButton key='maximize' tabSet={tabSet}/>
      //   )
      // }
      //renderValues.buttons.push(<DeleteTabSetButton tabSet={tabSet} key='delete'/>)
      renderValues.buttons.push(<ChooseTabOnTabSetDropdownMenuButton tabSet={tabSet} key='chooseTab'/>)
    }

    // renderValues.headerContent = <div>content</div>
    // On the left sidebar we'd want to be able to collapse it to the topbar, double click the separator to reset width, etc
  }

  @action onGlobalSettingsUpdate_TrackVersionInReport = (clonedSettings: ICatalogUserSettings) => {
    const {reportId} = this
    if (reportId) {
      const {changesByReportId} = this,
        newVersion = {settings: clonedSettings, timestamp: new Date()}

      if (!changesByReportId[reportId]) {
        changesByReportId[reportId] = {
          versionIndex: 0,
          versions: [
            newVersion
          ]
        }
      } else {
        const r = changesByReportId[reportId]
        r.versionIndex = r.versions.length
        r.versions.push(newVersion)
      }
    }
  }

  @computed get currentReport() { return this.stores.reports.currentReport }

  @computed get reportId() { return this.catalog.idFor(this.currentReport)}

  @observable changesByReportId: { [id: string]: IReportChanges } = {}

  @computed get borderLeft() {
    return this.model?.borderSet?.borders?.find(b => b.location.name === 'left')
  }

  @computed get borderRight() {
    return this.model?.borderSet?.borders?.find(b => b.location.name === 'right')
  }

  @computed get borderTop() {
    return this.model?.borderSet?.borders?.find(b => b.location.name === 'top')
  }

  @computed get borderBottom() {
    return this.model?.borderSet?.borders?.find(b => b.location.name === 'bottom')
  }

  @computed get isShowingNoBorders() {
    const {isShowingTopBorder, isShowingRightBorder, isShowingBottomBorder, isShowingLeftBorder} = this
    return !isShowingTopBorder && !isShowingRightBorder && !isShowingBottomBorder && !isShowingLeftBorder
  }

  @computed get isShowingAllBorders() {
    const {isShowingTopBorder, isShowingRightBorder, isShowingBottomBorder, isShowingLeftBorder} = this
    return isShowingTopBorder && isShowingRightBorder && isShowingBottomBorder && isShowingLeftBorder
  }

  @computed get isShowingLeftBorder() { return this.borderLeft != null && this.borderLeft.isShowing}

  @computed get isShowingRightBorder() { return this.borderRight != null && this.borderRight.isShowing}

  @computed get isShowingTopBorder() { return this.borderTop != null && this.borderTop.isShowing}

  @computed get isShowingBottomBorder() { return this.borderBottom != null && this.borderBottom.isShowing}

  @action toggleBorder = (location: IBorderLocation | 'all' | 'none') => {
    const {model, model: {borderSet: {borders}}} = this

    if (location === 'all') {
      const {isShowingLeftBorder, isShowingTopBorder, isShowingRightBorder, isShowingBottomBorder, toggleBorder} = this
      !isShowingLeftBorder && toggleBorder('left')
      !isShowingTopBorder && toggleBorder('top')
      !isShowingRightBorder && toggleBorder('right')
      !isShowingBottomBorder && toggleBorder('bottom')
    } else if (location === 'none') {
      const {toggleBorder, isShowingLeftBorder, isShowingTopBorder, isShowingRightBorder, isShowingBottomBorder} = this
      isShowingLeftBorder && toggleBorder('left')
      isShowingTopBorder && toggleBorder('top')
      isShowingRightBorder && toggleBorder('right')
      isShowingBottomBorder && toggleBorder('bottom')
    } else {
      const border = borders.find(b => b.location.name === location)

      if (!border) {
        borders.push(BorderNode._fromJson({
          type: 'border',
          location,
          show: true,
          children: []
        }, model))
      } else {
        border.attributes.show = border.attributes.show === false ? true : false
      }
      model.layout.store.asyncDoLayout()
    }
  }

  @computed get contextMenuItems() {
    const {stores: {contextMenu: {extra}}} = this,
      id = extra?.node?.id,
      catalogStoreRoot = extra?.node?.catalogRoot,
      tabStore = (extra?.vipStore ?? extra.node?.vipStore as VipFlexLayoutTabStore) // ?? new VipFlexLayoutTabStore(store.stores, {flexNode: extra.node})  // Cannot create something in a computed

    return (
      <StoresInstanceContext.Provider value={catalogStoreRoot?.stores ?? globalStores}>
        <CatalogRootFlexLayoutStoreContext.Provider value={catalogStoreRoot}>
          <VipFlexLayoutTabStoreContext.Provider value={tabStore}>
            <FlexLayoutContextMenu/>
          </VipFlexLayoutTabStoreContext.Provider>
        </CatalogRootFlexLayoutStoreContext.Provider>
      </StoresInstanceContext.Provider>
    )
  }

  @action showFilterTab = () => {
    DEBUG_FLEX_LAYOUT && console.log(`showFilterTab`)
    this.toggleKnownTab('filters')
  }

  /**
   * Out of the box behavior only lets us have a single left tab showing in the border when it's generally much preferable to have a library/filter vertical split that can be resized
   * and that doesn't need to show tab borders.
   * @param v
   */
  @action toggleKnownTab = (v: FlexComponentName, finalState?: boolean) => {
    DEBUG_FLEX_LAYOUT && console.debug(`toggleKnownTab`, {v, finalState})

    if (!v) return
    const {model} = this,
      // tabId = `${v}${borderTabAsRealTabIdSuffix}`,
      borderTab = model.nodeById<TabNode<IFlexLayoutToolbarWindowConfig>>(v),
      disabled = this.isDisabled(borderTab)

    _.merge(borderTab._attributes, {config: {settings: {isToggledOn: disabled ? false : (finalState ?? borderTab.config?.settings?.isToggledOn !== true)}}})

    if (borderTab.attributes.config.settings.isToggledOn) {
      (borderTab.parent as BorderNode).isCollapsed = false
    }

    if (v === 'catalogAggregateDetails' && this.activeCatalogRoot) {
      const {activeCatalogRoot: {catalog: {settings, isGroupedView, groupBy: {firstGroup}}}} = this

      if (borderTab.config.settings.isToggledOn) {
        // Already showing aggs, they've clicked the border button

        // If we're showing and they clicked the border, they want to move from inline -> sidebar
        if (settings.aggregateLocation == 'inline') {
          settings.aggregateLocation = 'sidebar'
        } else {
          // They want to hide the tab - if they close the X in the tab should it switch to inline?
          settings.showAggs = false
          // settings.aggregateLocation = 'inline'
        }
      } else {
        settings.aggregateLocation = 'sidebar'
        settings.showAggs = true
        borderTab.config.settings.isToggledOn = true
        if (isGroupedView && _.isEmpty(settings.aggregateDetailsGroupPath)) {
          // Select the first group
          _.first(firstGroup.groupsInOrder)?.store?.selectGroup()
        }
      }

    }

    const {isShowingLibraries, isShowingFilters, isShowingCatalogItemDetailsTab, activeCatalogRoot} = this

    if (activeCatalogRoot) {
      const {storesToInject: {sidebar}} = activeCatalogRoot
      sidebar.left.open = isShowingLibraries || isShowingFilters
      sidebar.left.tab = isShowingLibraries ? 0 : 1

      sidebar.right.open = isShowingCatalogItemDetailsTab
    }
  }

  onDoubleClick_forTabNode = (node: TabNode) => (e: React.MouseEvent<HTMLDivElement>) => {
    DEBUG_FLEX_LAYOUT && console.log(`onDoubleClick_forTabNode`, node)
    //this.toggleTab(node.component)

    // node.isEnableRename && this.onStartRenameTab(node)

    if (true) { // maximize behavior
      const elem = e.target as HTMLElement
      if (elem.hasAttribute('data-interaction-kind') || elem.closest('.bp3-menu') != null) {
        return
      }

      const parentNode = node.parent as TabSetNode
      DEBUG_FLEX_LAYOUT && console.log(e)
      if (parentNode.canMaximize) {
        this.maximizeTab(node.id)
      }
    }
  }

  onContextMenu_forSplitter = (splitter: SplitterNode) => (e: React.MouseEvent<HTMLDivElement>) => {
    this.contextMenu.onComponentContextMenu(e, this, {splitter})
  }

  onContextMenu_forTabNode = (tab: TabNode) => (e: React.MouseEvent<HTMLDivElement>) => {
    // We have a hover menu instead for tabs
    this.contextMenu.onComponentContextMenu(e, this, {tab}, {className: css.groupLabelContextMenu})
    // if (false && node.isGroupLabel) { // Use hover menu except for tab group labels
    // }
    // else {
    //   e.stopPropagation()
    // }
  }

  onContextMenu_forBorderNode = (border: BorderNode) => (e: React.MouseEvent<HTMLDivElement>) => {
    this.contextMenu.onComponentContextMenu(e, this, {border})
  }

  onContextMenu_forTabSetNode = (tabSet: TabSetNode) => (e: React.MouseEvent<HTMLDivElement>) => {
    this.contextMenu.onComponentContextMenu(e, this, {tabSet})
  }

  isDisabled = (node: TabNode) => {
    if (!node) return false

    const c = node.component,
      {activeOrLastCatalogRoot: acr, activeTab, tabsByComponent: {pdfViewer}} = this

    if (c === 'catalogAggregateDetails') {
      if (!acr) return true
      return !acr.catalog?.aggregation?.hasAggregations
    }

    if (c == 'filters' || c == 'catalogItemDetails') return !acr

    if (c == 'pdfOptions') {
      return !acr?.catalog.isShowingClientPDF //&& (acr?.catalog?.settings?.viewMode !== 'pdf' || this.stores.cloudPDF.pendingJobs.length > 0 || !_.isEmpty(pdfViewer))
    }

    if (c === 'libraries' && (activeTab?.storesToInject?.catalog?.shouldRenderLibraryTab == false || _.isEmpty(this.catalogRoots))) return activeTab?.component != 'whiteboard'
    return false
  }

  // sanitizeJson = (json) => {
  //   deepdash.eachDeep(json, (v, k, parent, content) => {
  //     if ((k === 'active') || (k === 'id' && parent.type && v && v.startsWith('#'))) {
  //       delete parent[k]
  //     }
  //   })
  //   return json
  // }

  // @computed get sanitizedJson() {
  //   return this.sanitizeJson(_.cloneDeep(this.modelJson))
  // }

  omitFromModelDiff = ['global', 'borders']
  // todo - refactor below two functions and above 2 autoruns
  @action _updateLayoutFromJsonModel = () => {
    const {reports, modelJson, omitFromModelDiff} = this
    // console.trace()

    const diff = !this.model ? null : jsondiff.diff(_.omit(modelJson, omitFromModelDiff), _.omit(this.model?.asJson, omitFromModelDiff))

    if (diff || !this.model) {
      if (this.model && this.model.asJsonStringified == stringify(modelJson)) {
        DEBUG_FLEX_LAYOUT && console.debug(`Model is unchanged, using existing model`)
      } else {
        if (reports.justPublishedDraft) {
          reports.justPublishedDraft = false
          if (this.model) {
            return
          }
        }

        DEBUG_FLEX_LAYOUT && DEBUG && console.debug(`${this.model ? 'Reset' : 'Initialize'} flex layout from underlying JSON`, {diff})

        reports.justDiscardedDraft = false
        // _.values(this._catalogRoots).forEach(cr => {
        //   cr.dispose()
        //   cr.stores.dispose()
        // })
        // this._catalogRoots = {}

        this.onEndPreviewLayoutInteraction() // The user potentially clicked a change action
        this._updateSettingFromModelJson()
        const newModel = FlexLayout.Model.fromJson(modelJson, false, `_updateLayoutFromJsonModel`)
        this.model = newModel
        if (!reports.currentReportModel) {
          reports.currentReportModel = newModel
        }

        this._updateIsFromLayoutModel = true
        this.modelJson = newModel.asJson

        const {workspaces: {firstAvailableWorkspace}} = this
        if (firstAvailableWorkspace && _.isEmpty(this.model.firstTabSet?.children)) {
          this.create.newCatalogTab({
            workspace: firstAvailableWorkspace,
            config: {}
          })
        }

      }
    } else {
      DEBUG_FLEX_LAYOUT && console.log(`_updateLayoutFromJsonModel() - No changes detected, ignoring update`, {diff, modelJson})
    }
  }

  @observable _updatingSettings = false

  @action private _updateSettingFromModelJson = () => {
    const {model, modelJson} = this
    // console.debug(`Updating catalog.settings.layout.model - we do NOT want this to trigger a rerender, we're just trying to save`, modelJson)
    this._updatingSettings = true

    this.catalog.reportSettings.flexLayout.model = _.cloneDeep(_.omit(model?.asJson ?? modelJson, ['global']))
  }

  @computed get settings() {
    return this.catalog.reportSettings.flexLayout
  }

  @action onDoubleClickSplitter = (node: SplitterNode) => (e: React.MouseEvent<HTMLDivElement>) => {
    node.onDoubleClick()
  }

  /**
   * Note that borders won't be caught by this logic
   * @param id
   * @param removeParentIfEmpty
   */
  @action removeNodeFromParent = (node: FlexLayoutNode, removeParentIfEmpty = false) => {
    if (!node) {
      DEBUG_FLEX_LAYOUT && console.debug(`No node specified to remove`)
      return // likely already deleted by an earlier autorun
    }

    if (node === node.model.root) return

    const parentNode = node.parent
    node.delete()

    if (parentNode) {
      const parentId = parentNode.id

      parentNode.delete()
      if (!parentNode) {
        DEBUG_FLEX_LAYOUT && console.debug(`Unable to find parent to remove node from for ${node.id}`)
      }
      if (parentNode?._children) {
        parentNode._children = parentNode._children.filter(c => c.id !== node.id)
        //parentNode._removeChild(node)

        if (removeParentIfEmpty && _.isEmpty(parentNode.children)) {
          DEBUG_FLEX_LAYOUT && console.log(`removeNodeFromParent(${node}) - parent is empty, deleting as well - ${parentNode.id}`)
          this.removeNodeFromParent(parentNode, removeParentIfEmpty)
        }
      }
    }
  }

  @action closeOtherTabsInGroup = (tab: TabNode) => {
    const {tabSet} = tab

    tabSet._children = tabSet.children.filter(c => c.id === tab.id)
  }

  @observable.ref create: CreateFlexLayoutStore
  @action moveTabs = (tabs: Array<TabNode>, direction: MoveTabDirection) => {
    if (_.uniq(tabs.map(t => t.parent.id)).length !== 1) {
      throw new Error(`Ever tab must have the same parent for moveTabs()`)
    }

    tabs.forEach(t => this.moveTab(t, direction))
  }

  @action moveTab = (tab: TabNode, direction: MoveTabDirection) => {
    tab.move(direction)
  }

  @action changeSplitterOrientation = (node: TabNode | SplitterNode) => {
    DEBUG_FLEX_LAYOUT && console.debug(`Change splitter orientation ${node}`)

    const {newId} = this

    if (node instanceof TabNode) {
      const {tabSet: {row}} = node,
        grandParentNode = row.parent as RowNode

      // Are we already in a row of a row?  If so, just remove a level of row - I don't think is sufficient for multicolumn cases
      if (grandParentNode) {
        grandParentNode._children = row._children.slice()
        grandParentNode._children.forEach(n => n.parent = grandParentNode)
      } else {
        const columnJson: IJsonRowNode = {
          id: newId(),
          type: 'row',
          children: row.json.children
        }

        row._children = []
        row.addChildJson(columnJson)
      }
    } else {
      const row = node.parent as RowNode
      if (row.isHorizontal) {
        // Add a child row
        const newRow = {
            type: 'row',
            weight: 1 / (row.children.length + 1),
            id: guid(),
            children: [node.leftOrTop.asJson, node.rightOrBottom.asJson]
          } as IJsonRowNode,
          index = node.leftOrTop.indexInParent

        row._children.splice(index, 2, RowNode._fromJson(newRow, node.model))
      } else {
        const gp = row.parent as RowNode
        gp._children.splice(row.indexInParent, 1)
        row.children.forEach(c => gp.addChildJson(c.asJson))
      }
    }
  }

  @action moveToOppositeGroup = (tab: TabNode) => {
    const {tabSet, tabSet: {row}} = tab,
      {indexInParent} = tabSet,
      newIndex = indexInParent === 0 ? 1 : 0;

    (row.children[newIndex] as TabSetNode).addChildJson(tab.json)
    this.removeNodeFromParent(tab, true)
  }

  @action moveTabToNewWindow = (node: TabNode) => {

  }

  @action unsplit = (node: TabNode | DefaultNodeType) => {
    if (node instanceof SplitterNode) {
      node.delete()
    } else {
      const tabSet = node instanceof TabNode ? node.tabSet : node as TabSetNode,
        row = tabSet.parent as RowNode,
        {indexInParent} = tabSet

      if (indexInParent + 1 === tabSet.parent.children.length) {
        row._children.splice(indexInParent, 1);

        (_.last(row._children) as TabSetNode).addChildren(tabSet.children)
      } else {
        tabSet.addChildren(row.children[indexInParent + 1]?.children ?? [])
        row._children.splice(indexInParent + 1, 1)
      }
    }
  }

  @action maximizeTabSet = (id?: string) => {
    const node = this.model.nodeById<TabSetNode>(id)
    this.settings.maximizedTab = _.first(node?.children)?.id
  }

  @observable maximizingTabId: string
  @action maximizeTab = (id?: string) => {
    // console.log(`maximizeTab(${id})`)

    this.maximizingTabId = id
    this._setMaximizedTabIdDebounced(id)
  }

  private _setMaximizedTabIdDebounced = _.debounce((id) => {
    runInAction(() => {
      this.settings.maximizedTab = id
      this.maximizingTabId = null
    })
  }, 150)

  @observable loading = false

  @action restore = () => {
    this.isMaximized && this.toggleMaximize()
  }

  @action toggleMaximize = (node?: TabNode) => {
    const {model, isMaximized, settings} = this
    model.lastAction = 'FlexLayout_MaximizeToggle'

    DEBUG_FLEX_LAYOUT && console.log(`toggleMaximize(${node?.id})`, {isMaximized})
    if (isMaximized) {
      this.loading = true
      setTimeout(() => {
        runInAction(() => {
          this.loading = false
          this.model.lastAction = ''
          settings.maximizedTab = null
        })
      }, 50)
    } else {
      this.maximizeTab(node?.id ?? this.lastMaximizedTab?.id)
    }
  }

  @action togglePinTab = (tab: TabNode) => {
    const {indexInParent, tabSet} = tab,
      lastPinnedIndex = _.findLastIndex(tabSet._children, (c: TabNode) => c.attributes.config?.pin === true)

    tab.config.pin = tab.config.pin !== true
    if (tab.config.pin) {

      tabSet._children.splice(indexInParent, 1)
      tabSet._children.splice(lastPinnedIndex + 1, 0, tab)
    } else {
      if (lastPinnedIndex > indexInParent) {
        tabSet._children.splice(indexInParent, 1)
        tabSet._children.splice(lastPinnedIndex, 0, tab)
      }
    }
  }

  @action onStartRenameTab = (node: TabNode) => {
    // Delay this slightly so the context menu can close otherwise it steals focus from our textbox
    setTimeout(() => {
      DEBUG_FLEX_LAYOUT && console.debug(`startRename ${node.id}`)
      const {layout, layout: {store: {setEditingTab}}} = this
      setEditingTab(node)
    })
  }

  generatedIds = 0
  newId = () => {
    this.generatedIds++
    //return `C${1000 + _.keys(this.lookups.id).length + this.generatedIds}`
    return guid()
  }

  @observable.ref previewLayoutInteractionNode: FlexLayoutNode | Array<FlexLayoutNode>
  _setLayoutInteractionNodeTimeout: number

  @action onStartPreviewLayoutInteraction = <T extends FlexLayoutNode>(node: T | Array<T>, fn: (previewNode: T | Array<T>) => void) => {
    // console.groupCollapsed(`onStartPreviewLayoutInteraction`, this.previewLayoutInteractionNode, ' → ', node)
    this._setLayoutInteractionNodeTimeout && clearTimeout(this._setLayoutInteractionNodeTimeout)
    this._setLayoutInteractionNodeTimeout = window.setTimeout(() => {
      this._setLayoutInteractionNodeTimeout = null
      this.previewLayoutInteractionNode = node
      const json = _.cloneDeep({...this.modelJson, borders: []})

      const diff = jsondiff.diff(json, this.previewModelJson)

      if (diff) {
        this.previewModelJson = json
        this.previewModel = Model.fromJson(json, true, `Initial creation of preview layout json`)
      }

      const {previewModel} = this

      const previewNodeCopy = node instanceof SplitterNode
                              ? previewModel.nodeById<RowNode>(node.parent.id).drawChildren[node.indexInParent] as T// Splitters don't persist to JSON
                              : !node ? null : _.isArray(node) ? node.map(n => this.previewModel.nodeById<T>(n.id)) : this.previewModel.nodeById<T>(node.id)
      try {
        fn(previewNodeCopy)
      } catch (err) {
        console.error(err)
        this.previewLayoutInteractionNode = null
      }
    }, ui.settings.flexLayout.layoutPreviewMs)
  }

  onEndPreviewLayoutInteraction = (node?) => {
    // console.log(`onEndPreviewLayoutInteraction`, this.previewLayoutInteractionNode, ' -> null')
    // console.groupEnd()
    this._setLayoutInteractionNodeTimeout && clearTimeout(this._setLayoutInteractionNodeTimeout)
    this._setLayoutInteractionNodeTimeout = window.setTimeout(() => {
      this.previewLayoutInteractionNode = null
    }, ui.settings.flexLayout.layoutPreviewMs)
  }

  /*@action duplicateAndSplitTab = (tab: TabNode, direction: 'right' | 'down' | 'inline') => {
    const {tabSet, tabSet: {row: parentRow}} = tab,
      isPreview = tab._model === this.previewModel,
      orientation = parentRow.orientation?.name,
      newTab: Partial<IJsonTabNode> = {
        ..._.cloneDeep(_.omit(tab.asJson, 'id)')),
        name: tab.displayName.startsWith('Duplicate of ') ? `${tab.displayName} (1)` : `Duplicate of ${tab.displayName}`,
        id: this.newId(),
        previewNodeId: tab.id,
        className: isPreview ? 'preview' : undefined
      }

    //console.debug(`duplicateAndSplitTab`, toJS({isPreview, newTab, tab, direction, tabSet, parentRow, orientation}))

    if (!isPreview) {
      this._updateIsFromLayoutModel = false
    }

    switch (direction) {
      case 'down':
      case 'right': {
        const newTabset: IJsonTabSetNode = {
          type: 'tabset',
          selected: 0,
          // weight: 1,
          id: this.newId(),
          children: [newTab],
          classNameTabStrip: isPreview ? 'preview' : undefined
        } as IJsonTabSetNode

        if (direction === 'right') {
          if (orientation === 'vert') {
            // Move the child tabset into a new horizontal row with a 50/50 split
            /!*
            Before:
              X
              Y

            After:
              X Z
              Y Y
             *!/

            const indexToMove = tabSet.indexInParent

            parentRow.addChildJson({
              type: 'row',
              // weight: 1,
              id: this.newId(),
              children: [
                tabSet.json,
                newTabset
              ]
            } as IJsonRowNode, true, indexToMove)
          } else {
            // Add another tabset in this horizontal row
            /!*
            Before:
              Row
                Tabset
                  [x, y, z]
            After:
              Row:
                Tabset
                  [x,y,z]
                Tabset
                  [x']
             *!/
            parentRow.addChildJson(newTabset, true, tabSet.indexInParent + 1)
          }
        } else { //down
          if (orientation === 'vert') {
            // Add another tabset in this vertical column
            parentRow.addChildJson(newTabset, true, tabSet.indexInParent + 1)
          } else {
            // Move the child tabset into a new vertical column with a 50/50 split
            const indexToMove = tabSet.indexInParent

            if (indexToMove === -1) {
              throw new Error(`Unable to find ${tabSet.id} in ${parentRow.id}`)
            }

            parentRow.addChildJson({
              type: 'row',
              id: this.newId(),
              // weight: 1,
              children: [
                tabSet.json,
                newTabset
              ]
            } as IJsonRowNode, true, indexToMove)
          }
        }
        break
      }

      case 'inline':
        const existingIndex = tab.indexInParent
        if (existingIndex != -1) {
          tabSet.addChildJson(newTab, true, existingIndex + 1)
        } else {
          tabSet.addChildJson(newTab)
        }
        break
    }
  }
*/
  @computed get rootRow(): RowNode {
    return this.model.root
  }

  @computed get tabs(): IJsonTabNode[] {
    return this.model?.nodesByType?.tab ?? []
  }

  @computed get tabNodes(): TabNode[] {
    if (!this.model) return []

    const {model: {nodeById}} = this
    return this.tabs.map(t => nodeById(t.id))
  }

  @computed get tabsNotInBorder() {
    return this.tabs.filter(t => this.model.nodeById(t.id)?.parent?.type === 'tabset')
  }

  @computed get tabSets(): Partial<TabSetNode>[] {
    return this.model?.nodesByType?.tabset ?? []
  }

  @action tileWindows = (isPreview?: boolean) => {
    const {model, model: {root}} = this.modelAndJsonFor(isPreview),
      tabs = model.nodesByType.tab,
      tabsNotInBorder = tabs.filter(t => this.model.nodeById(t.id)?.parent?.type === 'tabset'),
      catalogTabs = tabsNotInBorder.filter(t => t.component === 'catalog'),
      nonCatalogTabs = tabsNotInBorder.filter(t => t.component !== 'catalog')

    const finalPaneCount = Math.min(4, catalogTabs.length)

    if (catalogTabs.length <= 1) return

    const otherCatalogTabs = catalogTabs.slice(finalPaneCount)
    // console.log(toJS({catalogTabs, otherCatalogTabs, tabSets, finalPaneCount, rootRow}))

    root.children.forEach(v => v.removeFromParent())
    switch (finalPaneCount) {
      case 2:
        /**
         *  Before [A,B]
         *  After [A][B]
         */
        root.addChildJson(
          {
            type: 'row',
            children: [
              {
                type: 'tabset',
                children: [catalogTabs[0], ...otherCatalogTabs, ...nonCatalogTabs]
              },
              {
                type: 'tabset',
                children: [catalogTabs[1]]
              }
            ]
          } as IJsonRowNode)
        break

      case 3:
        /**
         *  Before: [A, B, C]
         *  After: [A][B][C]
         */
        root.addChildJson({
          type: 'row',
          children: [
            {
              type: 'tabset',
              children: [catalogTabs[0], ...otherCatalogTabs, ...nonCatalogTabs]
            },
            {
              type: 'tabset',
              children: [catalogTabs[1]]
            },
            {
              type: 'tabset',
              children: [catalogTabs[2]]
            }
          ]
        } as IJsonRowNode)
        break
        break

      case 4:
        /**
         *
         *  Before: [A, B, C, D]
         *  After: [A][B]
         *         [C][D]
         *
         *  Before [A,B][C,D]
         *  After[A][B]
         *       [C][D]
         *
         *  Before [A,D][B]
         *         [C    C]
         *  After  [A][B]
         *         [C][D]
         */
        root.addChildJson({
          type: 'row',
          children: [
            {
              type: 'row',
              children: [
                {
                  type: 'tabset',
                  children: [catalogTabs[0], ...otherCatalogTabs, ...nonCatalogTabs]
                },
                {
                  type: 'tabset',
                  children: [catalogTabs[1]]
                }

              ]
            },
            {
              type: 'row',
              children: [
                {
                  type: 'tabset',
                  children: [catalogTabs[2]]
                },
                {
                  type: 'tabset',
                  children: [catalogTabs[3]]
                }
              ]
            }
          ]
        } as IJsonRowNode)
        break
    }
  }

  modelAndJsonFor = computedFn((isPreview: boolean): { model: Model, modelJson: IJsonModel } => {
    return isPreview ? {model: this.previewModel, modelJson: this.previewModelJson} : {model: this.model, modelJson: this.modelJson}
  })

  isSidebarTab = (tab: IJsonTabNode) => {
    const {id, component} = tab
    return _.includes(this.mainWindowFlexComponents, component)
  }

  @action combineAllTabGroups = (isPreview = false) => {
    const {model: {nodesByType}, modelJson} = this.modelAndJsonFor(isPreview),
      {isSidebarTab} = this

    DEBUG_FLEX_LAYOUT && console.debug(`combineAllTabGroups`, isPreview, {modelJson})

    const tabsToMove = nodesByType.tab.filter(t => !isSidebarTab(t))

    if (tabsToMove.length > 1) {
      const tabSet = _.first(tabsToMove).tabSet

      for (var tab of tabsToMove) {
        if (!isSidebarTab(tab) && tab.tabSet !== tabSet) {
          tab.moveTo(tabSet)
        }
      }
    } else {
      DEBUG_FLEX_LAYOUT && console.debug(`Not enough tabs to combine - this menu shouldn't have been clickable`)
    }
  }

  @action closeTabsToTheLeft = (node: TabNode) => {
    const parent = node.tabSet, index = node.indexInParent

    if (index == -1) {
      // console.log({parentJson, node})
      throw new Error(`Unable to find ${node.name} in parent tabset`)
    } else {
      parent._children = _.take(parent._children, index)
      parent.attributes.selected = 0
    }
  }

  @action closeTabsToTheRight = (node: TabNode) => {
    const parent = node.tabSet, index = node.indexInParent

    if (index == -1) {
      // console.log({parentJson, node})
      throw new Error(`Unable to find ${node.name} in parent tabset`)
    } else {
      parent._children = _.take(parent._children, index + 1)
      parent.attributes.selected = index
    }
  }

  renderNamedComponent = (node: TabNode): React.ReactNode => {
    if (!node) return null

    if (node.isGroupLabel) return <div data-type='group' data-group={node.id}/>

    const {attributes: {component, config}} = node,
      tabConfig = config as IFlexLayoutCatalogRootConfig,
      props: IFlexLayoutTabProps & { key: string } = {
        ...node.config,
        flexNode: node,
        isLayoutPreview: node.model === this.previewModel,
        key: node.id
      }

    let instance
    switch (component) {
      case 'catalogItemDetails':
        instance = <FlexLayoutCatalogItemDetailsPane {...props} className={css.sidebarDetails}/>
        break
      case 'inlineCatalogItemDetails':
        instance = <FlexLayoutInlineCatalogItemDetailsPane {...props} className={css.inlineDetails}/>
        break

      case 'libraries':
        instance = <CatalogLibrariesFlexLayout {...props} />
        break

      case 'filters':
        instance = <CatalogFiltersFlexLayout {...props} />
        break

      case 'catalog':
        instance = <CatalogRootFlexLayoutTab {...props} />
        break

      // case ids.dataGrid
      //     DataGridRootContent: props => <CatalogDataGridView className={css.dataGridRoot} {...props} />,
      // CatalogFooter: props => <CatalogFooter/>,

      case 'catalogAggregateDetails':
        instance = <AggregationsFlexLayout {...props} />
        break

      case 'iframe':
        instance = <IframeFlexLayoutTab {...props} />
        break

      case 'jira':
        instance = <JiraRootPanel className={css.jiraPanel} {...props} />
        break

      case 'pdfOptions':
        instance = <PdfOptionsPanel {...props} className={css.pdfOptions} />
        break

      case 'flexLayoutSettings':
        instance = <FlexLayoutToolboxTabWindow/>
        break

      case 'c8':
        instance = <C8FlexLayoutTab {...props} />
        break

      case 'vipBuying':
        instance = <BuyingBoardFlexLayoutTab {...props} />
        break

      case 'whiteboard':
        instance = <WhiteboardFlexLayoutTab {...props} />
        break

      case 'pdfViewer':
        instance = <PDFViewerFlexLayoutTab {...props} />
        break

      case 'change-log':
      case 'change-log-tool-window':
        instance = <ChangeLogFlexLayoutTab {...props} />
        break

      default:
        instance = <div className={css.unknownComponent}>Unknown component:<br/>'{component}'</div>
        break
    }

    return <Suspense fallback={null}>
      <VipFlexLayoutTab key={`${node.id}-${node.storesToInject?.storeId}`} {...props}>
        {instance}
      </VipFlexLayoutTab>
    </Suspense>
  }

  // Super slow - walks the entire json object!
  // lookupsFor = computedFn((json) => {
  //   let r: FlexLayoutLookups = {
  //     id: {},
  //     component: {},
  //     flexType: {}
  //   }
  //
  //   if (!json) return r
  //
  //   deepdash.eachDeep(json, (v, k, parent) => {
  //     if (k === 'location' && parent.type === 'border') {
  //       r.id[`border_${v}`] = parent // this border_right stuff is what the flexlayout library internally uses for the id but doesn't give us such a property on the toJson() version it generates for borders...
  //     } else if (k === 'id' && parent.type !== 'border') {
  //       r.id[toJS(v)] = parent
  //     } else if (k === 'component') {
  //       r.component[toJS(v)] = [...(r.component[v] ?? []), parent]
  //     } else if (k === 'type') {
  //       r.flexType[toJS(v)] = [...(r.flexType[v] ?? []), parent]
  //     }
  //   })
  //
  //   return r
  // })

  @action clearPreviewLayoutInteractionNode = () => {
    this.previewLayoutInteractionNode = null
  }

  defaultCatalogNameFor = (node: TabNode) => {
    const {component} = node, {strings, catalog, workspaces} = this

    if (component === 'catalog') {
      const config = node.config as IFlexLayoutCatalogRootConfig,
        {catalogRoot} = node

      // Ungrouped -> 'Merchandising'

      if (!config || !catalogRoot) return node.name

      const {catalog: {presets: {selectedPreset}, presets, assortments, workspace: {config: wsConfig}, groupBy: {hierarchyGroups}}} = catalogRoot,
        validGroups = hierarchyGroups.filter(v => v && !v.isCatalogItemsGrouping)

      // console.log(workspace, hierarchyGroups, viewMode)

      if (selectedPreset) {
        return `${selectedPreset.label}${presets.isDirty ? '*' : ''}`
      }

      const title = wsConfig?.shortTitle ?? wsConfig?.title

      let r = `${_.isEmpty(validGroups) ? wsConfig?.title : `${title} → ${_.last(validGroups)?.title}`}` //`${title} → ${hierarchyGroups.map(hg => hg?.title ?? '...').join(' → ')}`}` //${viewMode == 'card' ? '' : ` (${strings.catalogViewModeFor(viewMode)})`}

      return `${r}${assortments?.selectedAssortment ? `\n(${assortments?.selectedAssortment.title ?? strings.assortment})` : ''}`
    }

    if (node.config?.nonDefaultName !== true) {
      switch (component) {
        case 'c8':
          return node.name || 'C8'
        case 'vipBuying': {
          const {settings: {url}} = node.config as IFlexLayoutIFrameTabConfig
          if (url === BuyingBoardUrl.retail) return 'Buying (Retail)'
          if (url === BuyingBoardUrl.wholesale) return 'Buying (Wholesale)'

          return 'Buying Board'
        }
        case 'catalogItemDetails': {
          return `${pluralize(node.catalogRoot?.catalog?.catalogItemName, 1) ?? 'Item'} Details`
        }
        case 'inlineCatalogItemDetails': {
          const {extraData} = node
          if (extraData?.store?.overriddenStores) {
            const store = extraData.store as FlexLayoutInlineCatalogItemDetailsPaneStore

            const {overriddenStores: {sidebar}} = store
            if (sidebar) {
              const {right, right: {navItems, path}} = sidebar
              if (path.length <= 1 && right.details?.data) {
                return _.get(right.details.data, right.details.dataMap.title)
              } else if (path.every((p, i) => !_.isEmpty(navItems[i]?.data))) {
                return path.map((p, i) => navItems[i].title).join(' ➔ ')
              }
            }
          }
          return node.name
        }

        case 'filters':
        case 'libraries':
          if (node.isInBorder) return node.name
          else return `${node.name}${node.catalogRoot ? ` (${node.catalogRoot.catalog.shortTitle})` : ``}`

        default: {
          return node.name ?? toHeaderCase(component)
        }
      }
    }

    return node.name ?? strings.emptyLabel
  }

  /// region Tab Group Actions

  @action deleteGroup = (tab: TabNode) => {
    const {removeNodeFromParent} = this
    tab.tabsInGroup.forEach(t => {
      t.tabGroup = undefined
    })
    removeNodeFromParent(tab)
  }

  @action closeAllItemsInGroup = (tab: TabNode) => {
    const {model, removeNodeFromParent} = this
    tab.tabsInGroup.forEach(t => {
      removeNodeFromParent(model.nodeById(t.id))
    })
    removeNodeFromParent(tab)
  }

  @action newTabInGroup = (groupTab: TabNode, selectTab = false) => {
    const {model, tabSet, lastContiguousGroupTabIndex} = groupTab
    const pos = tabSet.addChild(
      TabNode._fromJson({
        ...this.presetsStore.cardTab,
        id: guid(),
        tabGroup: groupTab.id
      } as IJsonTabNode, model),
      lastContiguousGroupTabIndex + 1)

    if (selectTab) {
      tabSet._setSelected(pos)
    }

    model._changeListener && model._changeListener()
    DEBUG_FLEX_LAYOUT && console.debug(`Create new tab in group ${groupTab.name}`)
  }

  @action moveToNewWindow = (tab: TabNode) => {
    console.log(`NYI - moveToNewWindow`, {tab})
  }
  /// endregion

  @action setActiveTab = (tab: TabNode) => {
    const {tabSet, indexInParent} = tab, {isMaximized, knownTabs} = this

    tabSet?.model?._setActiveTabset(tabSet)
    tabSet?._setSelected(indexInParent)

    // Don't track non-content windows as 'active' tabs
    if (knownTabs[tab.id] || knownTabs[tab.component]) return

    if (isMaximized) {
      this.maximizedTab = tab
    } else {
      this.activeTab = tab
    }

    this.model.onChange()
  }

  @action onNewMaximizedTab_Workspace = (e: React.FormEvent, workspace: WorkspaceConfig) => {
    const {create, maximizeTab, model: {firstTabSet}} = this
    DEBUG_FLEX_LAYOUT && console.log(`Create new tab and maximize - ${workspace._id}`)

    if (firstTabSet) {
      const tab = create.newCatalogTab({view: 'card', parentTabSet: firstTabSet, selectNewTab: true, workspace})
      tab && maximizeTab(tab.id)
    } else {
      DEBUG_FLEX_LAYOUT && console.debug(`Unable to find tabset in which to create new maximized tab.`)
    }
  }

  @action createDefaultModel = () => {
    this.catalog.reportSettings.flexLayout.model = _.cloneDeep(this.presetsStore.defaultModel)
  }
}

export const FlexLayoutStoreContext = React.createContext<FlexLayoutStore>(null)
export const useFlexLayoutStore = () => React.useContext(FlexLayoutStoreContext)

/// endregion

/* Flex Layout Presets - Replaced by Reports

  // @computed get presetSearchBody() {
  //   const {stores: {presets: {presetSearchBody}}} = this,
  //     r: any = _.cloneDeep(presetSearchBody)
  //
  //   if (!presetSearchBody) return null
  //
  //   r.query.bool.filter = [
  //     ...(r.query.bool.filter ?? []), {
  //       exists: {field: 'layout'}
  //     }
  //   ]
  //
  //   return r
  // }

  // @observable presets: Array<ICatalogPresetES> = []
  //
  // @computed get presetById() { return _.keyBy(this.presets, p => p._id)}

  // @action loadPreset = (preset: string) => {
  //   console.groupCollapsed(`Reset Flex Layout to '${preset}'`)
  //
  //   const {presetById, modelJson, borderLeft, borderRight, toggleKnownTab, settings, presetsStore} = this
  //   settings.preset = preset
  //
  //   const esPreset = presetById[preset]
  //   if (esPreset) {
  //     this.modelJson = {...this.modelJson, ..._.pick(JSON.parse(esPreset.layout), 'layout')}
  //   } else {
  //     const {createAndShowDock, borderBottom} = this
  //     presetsStore.resetModelToDefault()
  //
  //     switch (preset) {
  //       case 'collapsed':
  //         break
  //
  //       case 'left-sidebar':
  //         toggleKnownTab('libraries')
  //         toggleKnownTab('filters')
  //         if (_.isEmpty(borderLeft.children)) borderLeft.show = false
  //         break
  //
  //       case 'pdf':
  //         modelJson.layout.children = [
  //           {
  //             type: 'tabset',
  //             children: [
  //               presetsStore.pdfTab
  //             ]
  //           }
  //         ]
  //         borderRight.selected = borderRight.children.length - 1
  //         borderRight.show = true
  //
  //         break
  //
  //       case 'dev':
  //       case 'qa':
  //         createAndShowDock('bottom')
  //         borderBottom.children.push(
  //           {
  //             type: 'tab',
  //             name: 'My Issues',
  //             config: {
  //               jiraPreset: 'my-issues'
  //             },
  //             component: 'jira'
  //           },
  //           {
  //             type: 'tab',
  //             name: 'Active Sprints',
  //             config: {
  //               jiraPreset: 'current-sprints'
  //             },
  //             component: 'jira'
  //           },
  //           {
  //             type: 'tab',
  //             name: 'New Issue',
  //             config: {
  //               jiraPreset: 'current-sprints'
  //             },
  //             component: 'jira'
  //           }
  //         )
  //         break
  //
  //       case 'qa':
  //         break
  //
  //       default: {
  //       }
  //     }
  //   }
  //
  //   console.groupEnd()
  // }

  @action loadLayoutPresetsFromEs = async () => {
    const {es, presetSearchBody} = this
    if (!presetSearchBody) return

    const {items} = es.unwrapHitsResult<ICatalogPresetES>(await es.search<ICatalogPresetES>({
      index: es.indexes.presets,
      body: presetSearchBody,
      meta: `Load user presets`
    }, {log: {query: false, result: false}}))

    this.presets = items
  }

  private autorun_loadLayoutPresetsFromEs = autorun(() => {
    if (!this._startAutoruns || !this.stores.authStore.userIsAuthenticated) return
    const {presetSearchBody} = this
    this.loadLayoutPresetsFromEs()
  }, {name: `Load layout presets`})

 */

/*

Should be done by autorun

@action private _addItemToSidebar = (tabOriginal: IJsonTabNode) => {
    const {borderTabSetAttributes, sidebarTabSetAttributes, model, borderForKnownElement} = this,
      tab = {...tabOriginal, enableClose: true},
      location = borderForKnownElement[tabOriginal.component]

    console.log(`_addItemToSidebar`, {tab, location})

    if (!location) throw new Error(`Can't add item, unknown item.  Add ${tabOriginal.id} to borderForKnownElement`)

    const id = `${location}Sidebar`

    const sidebar = model.getNodeById(id)
    if (!sidebar) {
      console.log(`No sidebar object, creating a tabset`)
      const newTabset: IJsonTabSetNode = {id, weight: .2, ...sidebarTabSetAttributes, ...(borderTabSetAttributes[tabOriginal.component] ?? {}), children: [tab]}

      if (location === 'left') {
        model.root.addChildJson(newTabset, 0)
      } else {
        model.root.addChildJson(newTabset)
      }
    } else if (sidebar instanceof TabSetNode) {
      // Convert to top/bottom row with two tabsets instead of adding the item to the tab as normal
      console.log(`Sidebar is a tabset, updating to be a row`)

      const parentRow = sidebar.parent as RowNode,
        newRow = {
          type: 'row',
          id,
          weight: .4,
          enableMaximize: false,
          children: [
            {
              ...sidebarTabSetAttributes,
              weight: FLEX_WEIGHT_DEFAULT,
              children: [
                ...(sidebar?.json?.children ?? []) //.map(v => _.omit(v, 'id')) // Remove tabset ID as before leftSidebar was a tabset but now it'll be a row
              ]
            } as IJsonTabSetNode,
            {
              ...sidebarTabSetAttributes,
              weight: FLEX_WEIGHT_DEFAULT,
              children: [tab]
            } as IJsonTabSetNode
          ]
        } as IJsonRowNode

      parentRow._children.forEach(n => n.attributes.weight = FLEX_WEIGHT_DEFAULT)
      //console.log(`About to splice JSON: `, {parentJson, newRow})
      const {indexInParent} = sidebar
      parentRow._children.splice(indexInParent)
      parentRow.addChildJson(newRow, indexInParent)
    } else {
      console.log(`Adding tabset to existing sidebar row`);

      (sidebar as RowNode).addChildJson({
        ...sidebarTabSetAttributes,
        weight: FLEX_WEIGHT_DEFAULT,
        children: [tab]
      })
    }
  }

  @action
  private _removeItemFromLeftSidebar = (component: string) => {
    const {modelJson} = this

    // if (leftSidebarJson) {
    //   _.remove(leftSidebarJson.children, c => c.id === id)
    //
    //   if (_.isEmpty(leftSidebarJson.children)) {
    //     modelJson.layout.children = modelJson.layout.children.filter(b => b.id !== leftSidebarJson.id)
    //   }
    // }

    // No guarantee we were in our original place so left-sidebar can easily be null or not be where the thing wanted is
    const containerNode = deepdash.findDeep(modelJson, (v, k) => k === 'children' && v.some(c => c.component === component), {leavesOnly: false})?.parent as any
    if (containerNode) {
      _.remove(containerNode.children, (c: any) => c.component === component)
    } else {
      debugger
    }
  }
 */



// WEBPACK FOOTER //
// ./src/stores/catalog/flexLayout/flexComponents/FlexLayoutStore.tsx